import React from "react";
import { Box, Typography, Button, Container, Chip } from "@mui/material";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../constants/images";

const RecruiterFeedback = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Container
      maxWidth="xxl"
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        direction: isArabic ? "rtl" : "ltr",
        pt: { xs: 3, sm: 4, md: 6, lg: 8 },
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
        backgroundImage: `url('${IMAGES.ZAYN_BG_IMAGE}')`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: {
          xs: "top center",
          sm: "top center",
          md: "top center",
        },
        backgroundSize: { xs: "150% auto", sm: "130% auto", md: "100% auto" },
        minHeight: { xs: "auto", sm: "auto", md: "auto" },
      }}
    >
      {/* Chip */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          maxWidth: { xs: "100%", md: "600px" },
          width: "100%",
        }}
      >
        <Chip
          label={t("recruiterFeedback.chip")}
          sx={{
            backgroundColor: "white",
            border: "1px solid #0206174D",
            mb: { xs: 1.5, sm: 2 },
            color: "#818E98",
            fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "16px" },
            fontWeight: "500",
            textTransform: "capitalize",
            px: { xs: 0.5, sm: 0.7, md: 0.7 },
            py: { xs: 0.7, sm: 1, md: 1.5, lg: 2.3 },
            fontFamily: "var(--font-outfit)",
            borderRadius: "40px",
            mt: { xs: "7vh", sm: "6vh", md: "8vh", lg: "10vh" },
          }}
        />
      </Box>

      {/* Main Heading */}
      <Typography
        sx={{
          fontSize: {
            xs: "22px",
            sm: "26px",
            md: "36px",
            lg: "35px",
            xl: "44px",
          },
          fontWeight: 600,
          mb: 0,
          color: "#020617",
          maxWidth: { xs: "100%", md: 600 },
          mt: { xs: "2%", md: "1%" },
          fontFamily: "var(--font-plusjakarta)",
          textAlign: "center",
          width: "100%",
          lineHeight: { xs: 1.2, md: 1.3 },
        }}
      >
        <span style={{ color: "#9048FB" }}>
          {t("recruiterFeedback.title1")}
        </span>
        {t("recruiterFeedback.title2")}
      </Typography>

      {/* Subtitle */}
      <Typography
        variant="body1"
        sx={{
          position: "relative",
          zIndex: 10,
          fontSize: {
            xs: "13px",
            sm: "14px",
            md: "18px",
            lg: "22px",
            xl: "25px",
          },
          color: "#02061799",
          fontFamily: "var(--font-outfit)",
          fontWeight: "400",
          maxWidth: { xs: "100%", md: 1000 },
          mt: { xs: "4%", sm: "3%", md: "1%" },
          textAlign: "center",
          mx: "auto",
          lineHeight: { xs: 1.5, sm: 1.55, md: 1.5 },
        }}
      >
        {t("recruiterFeedback.subtitle1")}
        <Link
          href="/zayn-ai-recruiter"
          style={{
            color: "#8B5CF6",
            textDecoration: "underline",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          {t("recruiterFeedback.subtitleLink")}
        </Link>
        {t("recruiterFeedback.subtitle2")}
      </Typography>

      {/* Button */}
      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          px: { xs: 0, sm: 0, md: 3 },
          display: "flex",
          justifyContent: { xs: "flex-start", sm: "center", md: "center" },
        }}
      >
        <Button
          href="/zayn-ai-recruiter"
          variant="contained"
          sx={{
            backgroundColor: "#9B58FF",
            color: "#fff",
            borderRadius: "25px",
            px: { xs: 2.5, sm: 2.8, md: 3 },
            py: { xs: 1, sm: 1.2, md: 1.5 },
            mt: { xs: "6%", sm: "5%", md: "3%" },
            textTransform: "none",
            fontSize: { xs: "13px", sm: "14px", md: "15px", lg: "16px" },
            fontWeight: 500,
            fontFamily: "var(--font-outfit)",
            boxShadow: "none",
            mb: { xs: 2, sm: 3, md: 4 },
            width: { xs: "100%", sm: "auto" },
            maxWidth: { xs: "100%", sm: "200px" },
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#000",
              boxShadow: "none",
              transform: "scale(1.05) translateY(-2px)",
            },
          }}
        >
          {t("recruiterFeedback.cta")}
        </Button>
      </Box>

      {/* Video */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: { xs: "350px", sm: "450px", md: "800px", lg: "500px" },
          height: { xs: "200px", sm: "240px", md: "500px", lg: "600px" },
          overflow: "hidden",
          mt: { xs: 2, sm: 3, md: 4, lg: -35 },
          zIndex: 1,
          display: { xs: "none", sm: "none", md: "none", lg: "block" },
        }}
      >
        <Box
          component="video"
          src={IMAGES.ZAYN_ORB_VIDEO}
          autoPlay
          title="Zayn AI recruiter - best AI recruiting tool working 24/7 in 50+ languages with recruitment automation software"
          aria-label="Zayn AI recruiter - best AI recruiting tool working 24/7 in 50+ languages with recruitment automation software"
          loop
          muted
          playsInline
          sx={{
            width: "100%",
            height: "auto",
            objectFit: "contain",
            position: "absolute",
            top: "53%",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      </Box>
    </Container>
  );
};

export default RecruiterFeedback;
